import { Button, Colors, InputGroup, Intent, NumericInput, Tag, Position } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import classNames from 'classnames'
import * as React from 'react'
import NullSafeNumericInput from '../../../../../components/NullSafeNumericInput'
import { TextWithOverflowTooltip } from '../../../../../components/TextWithOverflowTooltip'
import { UprightStyle } from '../../../../../types'
import { ProductVariant } from '../../../../../types/api'
import { pluralizeFromNumber } from '../../../../../utils/string'
import { validVariantPricing } from '../../../utils'
import { FormError, onVariantChangeHandler } from '../types'
import { hasVariantErrorsOutsideOffset, PRICE_OVERRIDES_DISABLED_PRICE_INPUT_TITLE_LABEL } from '../utils'
import { InventoryLocationSelect } from './inputs'
import { GutterTooltipError } from '../GutterTooltipError'

const styles: UprightStyle = {
  tableSpacing: { borderSpacing: '0 12px' },
  inventoryLocationColumn: { minWidth: 200 },
  newTag: { backgroundColor: Colors.GREEN3 },
  quantityContainer: { maxWidth: 130 },
  skuContainer: { maxWidth: 120 },
  priceContainer: { maxWidth: 140 },
  errorMsg: { fontSize: 10, margin: `5px 0 0 0`, color: Colors.RED2 },
  paginationError: { margin: `5px 0 0 0`, color: Colors.RED2, textAlign: 'center' },
  variantNewTagContainer: { marginRight: -15 }
}

interface Props {
  variants: ProductVariant[]
  draft: boolean
  onVariantChange: onVariantChangeHandler
  errors: FormError
  showGutterTooltipErrors?: boolean
  productOverridesActive?: boolean
}

const DEFAULT_INPUT_PROPS = {
  style: { height: 36 }, // this is unfortunately the easiest way right now to match the location input height
  fill: true
}

const MAX_PER_PAGE = 12

const VariantEditor = (props: Props) => {
  const { variants, draft, showGutterTooltipErrors, productOverridesActive } = props

  const [page, setPage] = React.useState<number>(0)
  const [priceError, setPriceError] = React.useState<number[]>([])
  const [compareAtPriceError, setCompareAtPriceError] = React.useState<number[]>([])
  const totalVariants = variants.length

  const hasSavedTableAtLeastOnce = variants.some(pv => !!pv.id)
  const maxPage = Math.floor(totalVariants / MAX_PER_PAGE)
  const startOfPageIndexOffset = page * MAX_PER_PAGE
  const endOfPageIndexOffset = page * MAX_PER_PAGE + MAX_PER_PAGE
  const errors = props.errors || {}

  function validateCompareAtPrice(idx: number) {
    const variant = variants[idx]

    if (validVariantPricing(variant)) {
      setCompareAtPriceError(s => s.filter(i => i !== idx))
    } else {
      // Null is okay but otherwise compareAtPrice must be greater than price
      setCompareAtPriceError(s => [...s, idx])
    }
  }

  const invalidPrices = React.useMemo(() => {
    const priceErrors = []
    if (draft) return priceErrors

    variants.forEach((variant, idx) => {
      const variantPricePositive = +variant.price > 0
      if (variantPricePositive) return

      priceErrors.push(idx)
    })
    return priceErrors
  }, [variants, draft])

  function validatePrice(idx: number) {
    const isInvalid = invalidPrices.some(i => i === idx) || false

    if (!isInvalid) {
      setPriceError(s => s.filter(i => i !== idx))
    } else {
      setPriceError(s => [...s, idx])
    }

    validateCompareAtPrice(idx)
  }

  // If variantsPrice is present in errors (locally or from the server) be sure to validate all the price fields
  React.useEffect(() => {
    if (errors?.localErrors?.variantsPrice || errors?.variantsPrice) {
      setPriceError(invalidPrices)
    }
  }, [errors?.localErrors?.variantsPrice, errors?.variantsPrice, invalidPrices])

  return (
    <div>
      <table className="table-reset table-variant-options table-variants" style={styles.tableSpacing}>
        <tr>
          <th>Variant</th>
          <th>Quantity</th>
          <th>SKU</th>
          <th>Price</th>
          <th>Compare at Price</th>
          <th>Location</th>
        </tr>

        {/* Variants may or may not have a unique ID, the index is good enough */}
        {/* eslint-disable react/no-array-index-key */}
        {variants.slice(startOfPageIndexOffset, endOfPageIndexOffset).map((pv, i) => {
          const idx = i + page * MAX_PER_PAGE // offset the variant index by the page in view
          let showGrouping = false

          if (i !== 0) {
            const currentOptionName = pv.title.split(' / ')[0]
            const nextOptionName = variants[i + 1]?.title?.split(' / ')[0]
            const lastOptionName = variants[i - 1].title.split(' / ')[0]

            // Add spacing if the next row != current row AND there's more than 1 in the group
            showGrouping = lastOptionName !== currentOptionName && nextOptionName === currentOptionName
          }

          const hasLocationError =
            errors[`variants_${idx}InventoryLocationId`]?.length > 0 && !pv.inventoryLocationId?.toString()
          const hasPriceError = priceError.includes(idx)
          const hasCompareAtPriceError = compareAtPriceError.includes(idx)

          return (
            <tr key={idx} className={`${showGrouping ? 'table-variants-row-separator' : ''}`}>
              <td className="table-variants-title">
                <TextWithOverflowTooltip label={pv.title} tooltipContent={pv.title} />
                <div style={styles.variantNewTagContainer}>
                  {!pv.id && hasSavedTableAtLeastOnce && (
                    <Tag round style={styles.newTag}>
                      New
                    </Tag>
                  )}
                </div>
              </td>
              <td>
                <div style={styles.quantityContainer}>
                  <NumericInput
                    {...DEFAULT_INPUT_PROPS}
                    min={0}
                    selectAllOnFocus
                    buttonPosition="none"
                    minorStepSize={1}
                    clampValueOnBlur
                    value={pv.quantity}
                    onValueChange={valueAsNumber => {
                      // valueAsNumber will be NaN when the user types a minus.
                      // We could handle this by maintaining the quantity state as a string and parsing when needed, but this is simpler.
                      // Users don't need to type negatives or decimals, so this is fine.
                      if (!Number.isNaN(valueAsNumber)) {
                        props.onVariantChange(idx, 'quantity', valueAsNumber)
                      }
                    }}
                  />
                </div>
              </td>
              <td>
                <div style={styles.skuContainer}>
                  <InputGroup
                    {...DEFAULT_INPUT_PROPS}
                    value={pv.sku}
                    placeholder="201956"
                    onChange={e => props.onVariantChange(idx, 'sku', e.target.value)}
                  />
                </div>
              </td>
              <td>
                <div style={styles.priceContainer}>
                  <GutterTooltipError
                    isOpen={hasPriceError && showGutterTooltipErrors}
                    offsetPx={556}
                    position={Position.LEFT}
                  >
                    <NullSafeNumericInput
                      {...DEFAULT_INPUT_PROPS}
                      className="large-input-icon-centering"
                      selectAllOnFocus
                      min={0}
                      intent={hasPriceError ? Intent.DANGER : Intent.NONE}
                      buttonPosition="none"
                      minorStepSize={0.01}
                      leftIcon={IconNames.DOLLAR}
                      value={pv.price}
                      onBlur={() => validatePrice(idx)}
                      onValueChange={(_, v) => {
                        props.onVariantChange(idx, 'price', v)
                      }}
                      title={productOverridesActive && PRICE_OVERRIDES_DISABLED_PRICE_INPUT_TITLE_LABEL}
                      disabled={productOverridesActive}
                    />
                  </GutterTooltipError>

                  {hasPriceError && <p style={styles.errorMsg}>Price is required</p>}
                </div>
              </td>
              <td>
                <div style={styles.priceContainer}>
                  <GutterTooltipError
                    isOpen={hasCompareAtPriceError && showGutterTooltipErrors}
                    offsetPx={363}
                    position={Position.RIGHT}
                  >
                    <NullSafeNumericInput
                      {...DEFAULT_INPUT_PROPS}
                      className="large-input-icon-centering"
                      selectAllOnFocus
                      min={0}
                      intent={hasCompareAtPriceError ? Intent.DANGER : Intent.NONE}
                      minorStepSize={0.01}
                      leftIcon={IconNames.DOLLAR}
                      buttonPosition="none"
                      value={pv.compareAtPrice}
                      onBlur={() => validateCompareAtPrice(idx)}
                      onValueChange={(_, vstr) => {
                        props.onVariantChange(idx, 'compareAtPrice', vstr)
                      }}
                      title={productOverridesActive && PRICE_OVERRIDES_DISABLED_PRICE_INPUT_TITLE_LABEL}
                      disabled={productOverridesActive}
                    />
                  </GutterTooltipError>
                  {hasCompareAtPriceError && (
                    <p style={styles.errorMsg}>Compare at Price must be more than the price</p>
                  )}
                </div>
              </td>
              <td
                style={styles.inventoryLocationColumn}
                // We can't style the React-Select directly so we apply a parent css style on validation error
                className={classNames({ 'inventory-location-error': hasLocationError })}
              >
                {/* TODO(jaxgeller) this will trigger an api call per row up to MAX_PER_PAGE */}
                <InventoryLocationSelect
                  hideLabel
                  value={pv.inventoryLocationId?.toString()}
                  onChange={e => {
                    props.onVariantChange(idx, e.name, e.value)
                  }}
                  error={hasLocationError}
                  errorTooltipOffset={showGutterTooltipErrors && 126}
                />
                {hasLocationError && (
                  <p style={styles.errorMsg}>{errors[`variants_${idx}InventoryLocationId`].join(', ')}</p>
                )}
              </td>
            </tr>
          )
        })}
        <tr className="table-variants-summary-separator">
          <td>
            <strong>
              {totalVariants} Total Variant{pluralizeFromNumber(totalVariants)}{' '}
            </strong>
          </td>
          <td>
            <strong>{variants.map(pv => pv.quantity).reduce((acc, v) => acc + v, 0)} Total Quantity</strong>
          </td>

          {/* Intentionally blank for spacing */}
          <td />
          <td />
          <td />
        </tr>
      </table>

      {totalVariants > MAX_PER_PAGE && (
        <>
          <div className="table-variants-pagination">
            <Button onClick={() => setPage(page - 1)} disabled={page === 0}>
              Previous
            </Button>
            <p>
              {startOfPageIndexOffset + 1}-{Math.min(endOfPageIndexOffset, totalVariants)} of {totalVariants} Variants
            </p>
            <Button onClick={() => setPage(page + 1)} disabled={page === maxPage}>
              Next
            </Button>
          </div>
          {hasVariantErrorsOutsideOffset(errors, endOfPageIndexOffset) && (
            <p style={styles.paginationError}>Variant errors on another page preventing product from being saved</p>
          )}
        </>
      )}
    </div>
  )
}

export default VariantEditor
